import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import type { FramingHost } from "./cameraFraming";
import { InsetEase } from "./cameraFraming";
import { type CameraView, cellNdc, cellToPx, clampLook, groundHit, halfGround, screenAxes, terrainHit, toViewport } from "./cameraProject";

/** Bounds on the look point at a zoom: the point moved inside them, or null when it already is. */
export type LookClamp = (x: number, z: number, zoom: number) => { x: number; z: number } | null;

/** A free camera pose (CameraCore.setShot): world positions, vertical field of view in degrees, key-light yaw. */
export interface CameraShot {
  readonly eye: { readonly x: number; readonly y: number; readonly z: number };
  readonly at: { readonly x: number; readonly y: number; readonly z: number };
  readonly fov: number;
  readonly yaw: number;
}

/**
 * The level camera's view state and projections (isoCamera.ts IsoCamera builds on it): the viewport and the fit
 * distance, the pan, zoom and yaw, the UI insets, the pan clamp at the map edge, and every viewport ⇄ ground and
 * world → viewport projection. The touch controls, shake and placement follow are IsoCamera's.
 */
export class CameraCore {
  static readonly Tilt = 35;
  /** Zoom on level start: about seven cells across a phone screen, like the reference art. */
  static readonly DefaultZoom = 0.38;
  /** Closest zoom (about four and a half cells across). */
  static readonly MinZoom = 0.24;
  /** Farthest zoom: the whole map fits. */
  static readonly MaxZoom = 1;
  /** Cells of shoreline that may show beyond the last row of tiles at the screen edge. */
  static readonly EdgeMargin = 0.5;

  /** Narrow field of view for the near-orthographic look of the reference art. */
  static readonly Fov = 30;
  /**
   * The default turn, degrees: the camera orbits on the world's −x −z side (`place`), so it sees a tower model's +x +z
   * diagonal (models are turned by HQ_YAW) — the skin sockets' index 3 (skins/kit/sockets.ts CAMERA_FACING).
   */
  static readonly DefaultYaw = 45;

  readonly camera = new THREE.PerspectiveCamera(CameraCore.Fov, 1, 0.5, CameraCore.Far);
  protected centre = new THREE.Vector3();
  protected halfW = 1;
  protected halfH = 1;
  protected yaw = CameraCore.DefaultYaw;
  protected targetYaw = CameraCore.DefaultYaw;
  protected zoom = CameraCore.DefaultZoom;
  protected readonly pan = new THREE.Vector3();
  protected width = 1;
  protected height = 1;
  /** The UI insets at the top and bottom, as reported and as the view is framed within this frame (eased). */
  protected readonly insetEase = new InsetEase();
  /** Distance that fits the unpanned grid; computed on frame/resize only so panning never changes the zoom. */
  protected fitDistance = 10;

  setViewport(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.refit();
  }

  get currentZoom(): number { return this.zoom; }
  /** Viewport height in CSS pixels (the insets are measured against it). */
  get viewportHeight(): number { return this.height; }
  /** Present yaw in degrees (45 at rest; snaps by 90 on rotate). */
  get currentYaw(): number { return this.yaw; }

  /** UI-covered pixels at the top and bottom of the viewport (HUD, card hand), applied at once (framing a new view). */
  setInsets(top: number, bottom: number): void { this.insetEase.set(top, bottom); this.apply(); }

  /**
   * The same, for a panel that changed size on a live view: the view eases to the new band over `InsetSmoothTime`
   * in `update` rather than jumping; a size that lasts only a frame or two (see `InsetHoldSeconds`) does not move it.
   */
  easeInsets(top: number, bottom: number): void { this.insetEase.ease(top, bottom); }

  /** The reported insets and the ones the view is framed within right now (they differ while the view eases). */
  get insets(): { top: number; bottom: number; framedTop: number; framedBottom: number } {
    const e = this.insetEase;
    return { top: e.top, bottom: e.bottom, framedTop: e.framedTop, framedBottom: e.framedBottom };
  }

  /** Fits the projected grid corners to 92% of the viewport with no pan; result cached in fitDistance. */
  protected refit(): void {
    const radius = Math.hypot(this.halfW, this.halfH);
    const vFov = THREE.MathUtils.degToRad(CameraCore.Fov);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    const fov = Math.min(vFov, hFov);
    let distance = (radius / Math.sin(fov / 2)) * 0.95;
    // the fit is the iso lens's (a free shot keeps its own pose): measured with the shot set aside
    const shot = this.shot;
    this.shot = null;
    for (let iter = 0; iter < 3; iter++) {
      this.place(this.centre, distance);
      let extent = 0;
      for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
        const corner = new THREE.Vector3(this.centre.x + sx * this.halfW, 0, this.centre.z + sz * this.halfH).project(this.camera);
        extent = Math.max(extent, Math.abs(corner.x), Math.abs(corner.y));
      }
      if (extent > 0) distance *= extent / 0.92;
    }
    this.fitDistance = distance;
    this.shot = shot;
    this.apply();
  }

  onPinch(scale: number): void { const r = this.zoomRange; this.zoom = Math.min(r.max, Math.max(r.min, this.zoom / scale)); this.apply(); }

  /**
   * A screen's own zoom range and look-point bounds (the hub's pinch, render/hubZoom.ts), on top of the map-edge clamp;
   * null restores the level's range and drops the bounds. `frame` clears both, so a new map never inherits them.
   */
  setZoomLimits(range: { readonly min: number; readonly max: number } | null, lookClamp: LookClamp | null = null): void {
    this.zoomLimits = range;
    this.lookClamp = lookClamp;
    if (range) this.zoom = Math.min(range.max, Math.max(range.min, this.zoom));
    this.apply();
  }
  /** The zoom range in effect: the screen's own, else MinZoom..MaxZoom. */
  get zoomRange(): { readonly min: number; readonly max: number } { return this.zoomLimits ?? { min: CameraCore.MinZoom, max: CameraCore.MaxZoom }; }
  protected zoomLimits: { readonly min: number; readonly max: number } | null = null;
  protected lookClamp: LookClamp | null = null;

  protected groundAt(px: number, py: number): THREE.Vector3 | null { return groundHit(this.view, px, py); }
  /** The camera and viewport, for the projections (cameraProject.ts). */
  private get view(): CameraView { return { camera: this.camera, width: this.width, height: this.height }; }

  /** Where a viewport point meets a height-field ground (cameraProject.ts terrainHit). */
  viewportToTerrain(px: number, py: number, height: (x: number, z: number) => number): { x: number; z: number } | null { return terrainHit(this.view, px, py, height); }

  /** World point → CSS pixels within the viewport. */
  worldToViewport(x: number, y: number, z: number): { x: number; y: number } { return toViewport(this.view, x, y, z); }

  /** Ground point at the centre of the screen (the look point). */
  lookPoint(): { x: number; z: number } { return { x: this.centre.x + this.pan.x, z: this.centre.z + this.pan.z }; }

  /** Ground cells spanning the screen width at the look point (orthographic estimate). */
  cellsAcross(): number { return 2 * this.halfScreenGround().right; }

  /** Ground cells spanning the screen height at the look point (orthographic estimate; foreshortened by the tilt). */
  cellsDeep(): number { return 2 * this.halfScreenGround().forward; }

  private halfScreenGround(): { right: number; forward: number } { return halfGround(this.camera, this.fitDistance * this.zoom, CameraCore.Tilt); }

  /** Clamps the pan so the band between the insets frames at most `EdgeMargin` cells beyond the map (cameraProject.ts clampLook). */
  private clampPan(top = this.insetEase.framedTop, bottom = this.insetEase.framedBottom): void {
    const look = this.centre.clone().add(this.pan);
    const clamped = clampLook(look, { centre: this.centre, halfW: this.halfW, halfH: this.halfH }, screenAxes(this.yaw), this.halfScreenGround(), this.height, top, bottom, CameraCore.EdgeMargin);
    if (clamped === null) return;
    this.pan.copy(clamped.sub(this.centre));
    this.pan.y = 0;
    this.place(this.centre.clone().add(this.pan), this.fitDistance * this.zoom);
  }

  /** The view's state as the framing (cameraFraming.ts) reads and moves it. */
  protected readonly framing: FramingHost = this.makeFraming();
  private makeFraming(): FramingHost {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const cam = this;
    return {
      camera: this.camera, centre: this.centre, pan: this.pan, insets: this.insetEase, minZoom: CameraCore.MinZoom, maxZoom: CameraCore.MaxZoom,
      get width() { return cam.width; }, get height() { return cam.height; },
      get zoom() { return cam.zoom; }, set zoom(z: number) { cam.zoom = z; },
      apply: (top, bottom) => this.apply(top, bottom), groundAt: (px, py) => this.groundAt(px, py),
    };
  }

  /** World position of a cell centre → normalised device coordinates. */
  projectCell(c: Cell): THREE.Vector3 { return cellNdc(this.view, c); }
  /** Cell → CSS pixels within a viewport of the configured size (origin top-left). */
  cellToViewport(c: Cell): { x: number; y: number } { return cellToPx(this.view, c); }

  /** Viewport pixel → cell on the y=0 plane, or null when the ray misses. */
  /** World XZ where a viewport point meets the ground plane (null above the horizon). */
  viewportToGround(px: number, py: number): { x: number; z: number } | null {
    const hit = this.groundAt(px, py);
    return hit === null ? null : { x: hit.x, z: hit.z };
  }

  viewportToCell(px: number, py: number): { x: number; y: number } | null {
    const hit = this.groundAt(px, py);
    if (hit === null) return null;
    return { x: Math.round(hit.x) + 0, y: Math.round(hit.z) + 0 };
  }

  /** Places the camera at the pan and zoom, clamped within the band the given insets leave (the framed ones by default). */
  protected apply(top = this.insetEase.framedTop, bottom = this.insetEase.framedBottom): void {
    this.place(this.centre.clone().add(this.pan), this.fitDistance * this.zoom);
    this.clampPan(top, bottom);
    const c = this.lookClamp?.(this.centre.x + this.pan.x, this.centre.z + this.pan.z, this.zoom);
    if (!c) return;
    this.pan.set(c.x - this.centre.x, 0, c.z - this.centre.z);
    this.place(this.centre.clone().add(this.pan), this.fitDistance * this.zoom);
  }

  /**
   * A free camera (the title's diorama, render/titleDiorama.ts): where the eye is, what it looks at and its vertical
   * field of view. While one is set it replaces the iso orbit; the shake still applies on top of it. `yaw` is the
   * direction the key light is aimed for (it rides the view's yaw: SceneRig.placeSun), held steady while the eye drifts
   * so the shadow map is not redrawn every frame. Null hands the view back to the iso framing at its own lens.
   */
  setShot(shot: CameraShot | null): void {
    // The view's own yaw is the player's (the 90° turns persist from screen to screen): a shot borrows it for its key
    // light and hands it back as it was. Leaving it at the shot's yaw turned every later hub and level off its 45°
    // diagonal (owner, 10-06: "you've broken the camera angle the game plays at").
    if (shot && !this.shot) this.yawBeforeShot = { yaw: this.yaw, target: this.targetYaw };
    if (!shot && this.yawBeforeShot) { this.yaw = this.yawBeforeShot.yaw; this.targetYaw = this.yawBeforeShot.target; this.yawBeforeShot = null; }
    this.shot = shot;
    const fov = shot ? shot.fov : CameraCore.Fov, far = shot ? CameraCore.ShotFar : CameraCore.Far;
    if (shot) this.yaw = this.targetYaw = shot.yaw;
    if (this.camera.fov !== fov || this.camera.far !== far) { this.camera.fov = fov; this.camera.far = far; this.camera.updateProjectionMatrix(); }
    if (shot) this.apply(); else this.refit();
  }
  get shotActive(): boolean { return this.shot !== null; }
  private shot: CameraShot | null = null;
  private yawBeforeShot: { yaw: number; target: number } | null = null;
  /** The iso lens's far plane, and a free shot's: the sky dome (r 400 round the camera) must not clip at a low tilt. */
  static readonly Far = 400;
  static readonly ShotFar = 2000;

  protected place(look: THREE.Vector3, distance: number): void {
    const shot = this.shot;
    if (shot) {
      this.camera.position.set(shot.eye.x, shot.eye.y, shot.eye.z);
      this.camera.up.set(0, 1, 0);
      this.camera.lookAt(shot.at.x, shot.at.y, shot.at.z);
      this.camera.updateMatrixWorld();
      return;
    }
    // Orbit position on the -X/-Z side so larger grid y (world z) is farther away, i.e. towards the top of the screen.
    const tilt = THREE.MathUtils.degToRad(CameraCore.Tilt);
    const yaw = THREE.MathUtils.degToRad(this.yaw);
    const offset = new THREE.Vector3(-Math.sin(yaw) * Math.cos(tilt), Math.sin(tilt), -Math.cos(yaw) * Math.cos(tilt)).multiplyScalar(distance);
    this.camera.position.copy(look).add(offset);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(look);
    this.camera.updateMatrixWorld();
  }
}
